import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import {
  AccessibilityRoute,
  CreatorRoute,
  HomeRoute,
  LibraryRoute,
  NotificationsRoute,
  PlayerRoute,
  ScenarioBuilderRoute,
  ScenarioLandingRoute,
  SearchRoute,
} from "./routes/routeViews";

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: HomeRoute,
});

const readerSearchSchema = zodValidator(
  z.object({ s: z.string().optional() }).catch({}),
);
const builderSearchSchema = zodValidator(
  z.object({ universeId: z.string().min(1).optional() }).catch({}),
);

const rootPathRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: readerSearchSchema,
  component: RootPathComponent,
});

function RootPathComponent() {
  const { s } = rootPathRoute.useSearch();
  return s === undefined ? <HomeRoute /> : <PlayerRoute />;
}

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/search",
  component: SearchRoute,
});
const accessibilityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accessibility",
  component: AccessibilityRoute,
});
const libraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/library",
  component: LibraryRoute,
});
const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notifications",
  component: NotificationsRoute,
});
const scenarioLandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scenarios/$scenarioId",
  component: ScenarioLandingRoute,
});
const playRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/play",
  component: PlayerRoute,
});
const playScenarioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/play/$scenarioId",
  component: PlayerRoute,
});

function ScenarioBuilderNestedRouteComponent() {
  const { universeId } = scenarioBuilderNestedRoute.useSearch();
  return (
    <ScenarioBuilderRoute
      universeId={
        typeof universeId === "string" && universeId.trim() !== ""
          ? universeId
          : null
      }
    />
  );
}

const scenarioBuilderNestedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/author/scenario-builder/$",
  validateSearch: builderSearchSchema,
  component: ScenarioBuilderNestedRouteComponent,
});
const creatorNestedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/author/$",
  component: CreatorRoute,
});

const legacyHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/creator/$",
  component: HomeRoute,
});
const legacyBuilderHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/scenario-builder/$",
  component: HomeRoute,
});

const routeTree = rootRoute.addChildren([
  rootPathRoute,
  searchRoute,
  accessibilityRoute,
  libraryRoute,
  notificationsRoute,
  scenarioLandingRoute,
  playRoute,
  playScenarioRoute,
  scenarioBuilderNestedRoute,
  creatorNestedRoute,
  legacyHomeRoute,
  legacyBuilderHomeRoute,
]);

export function createAppRouter() {
  return createRouter({
    routeTree,
    defaultNotFoundComponent: HomeRoute,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
